import React, { useEffect, useState } from "react";
import { triggerBookingPopup } from "../components/Header";
import { Link, useNavigate } from "react-router-dom";
import {
  FaTools,
  FaSnowflake,
  FaCheckCircle,
  FaArrowRight,
  FaWind,
} from "react-icons/fa";
import { adminApi } from "../utils/adminApi";

const fallbackServices = [
  {
    id: "washing",
    name: "Washing Machine Repair Service",
    icon: <FaTools />,
    color: "#0F52BA",
    types: [
      "Front Load Washing Machines",
      "Top Load Washing Machines",
      "Semi Automatic Machines",
    ],
    issues: ["Not spinning", "Water leakage", "Drum issues", "Power failure"],
  },
  {
    id: "fridge",
    name: "Refrigerator Repair Service",
    icon: <FaSnowflake />,
    color: "#FF6B00",
    types: [
      "Single Door Fridge",
      "Double Door Refrigerator",
      "Side-by-Side Fridge",
      "Mini Fridge",
    ],
    issues: [
      "Cooling issues",
      "Gas refilling",
      "Compressor problems",
      "Noise issues",
      "Ice buildup",
      "Water leakage",
      "Freezer cooling issue",
    ],
  },
  {
    id: "ac",
    name: "AC Repair Service",
    icon: <FaWind />,
    color: "#0F52BA",
    types: [
      "Split AC",
      "Window AC",
      "Inverter AC",
      "Smart AC",
    ],
    issues: [
      "Not cooling",
      "Gas leakage",
      "Compressor failure",
      "Water leakage",
    ],
  },
];

const Services = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState(fallbackServices);
  const [activeTab, setActiveTab] = useState(fallbackServices[0]);

  // Service to route mapping
  const serviceRoutes = {
    washing: "/washing-machine-service-bangalore",
    fridge: "/fridge-service-bangalore",
    ac: "/ac-service-bangalore",
  };

  const typeRoutes = {
    washing: {
      "Front Load Washing Machines":
        "/lg-front-load-washing-machine-service-bangalore",
      "Top Load Washing Machines":
        "/lg-top-load-washing-machine-service-bangalore",
      "Semi Automatic Machines": "/lg-washing-machine-service-bangalore",
    },
    fridge: {
      "Single Door Fridge": "/single-door-fridge-service-bangalore",
      "Double Door Refrigerator": "/double-door-refrigerator-service-bangalore",
      "Side-by-Side Fridge": "/side-by-side-fridge-service-bangalore",
      "Mini Fridge": "/mini-fridge-service-bangalore",
    },
  };

  const issueRoutes = {
    washing: {
      "Not spinning": "/lg-washing-machine-not-spinning",
      "Water leakage": "/lg-washing-machine-water-leak-repair",
      "Drum issues": "/lg-washing-machine-drum-repair",
      "Power failure": "/lg-washing-machine-no-power-issue",
    },
    fridge: {
      "Cooling issues": "/fridge-not-cooling-repair",
      "Cooling problem": "/fridge-not-cooling-repair",
      "Gas refilling": "/fridge-gas-refill-service-bangalore",
      "Gas leakage": "/fridge-gas-refill-service-bangalore",
      "Compressor problems": "/fridge-compressor-repair",
      "Compressor failure": "/fridge-compressor-repair",
      "Noise issues": "/fridge-noise-problem",
      "Ice buildup": "/fridge-ice-buildup-repair",
      "Water leakage": "/fridge-water-leak-repair",
      "Freezer cooling issue": "/freezer-cooling-issue",
    },
    ac: {
      "Not cooling": "/ac-not-cooling",
      "Cooling problem": "/ac-not-cooling",
      "Gas leakage": "/ac-gas-leakage",
      "Compressor failure": "/ac-compressor-repair",
      "Water leakage": "/ac-water-leak-repair",
    },
  };

  const getServiceKey = (service) => {
    const rawValue = `${service?.id || ""} ${service?.name || ""}`.toLowerCase();

    if (rawValue.includes("washing")) {
      return "washing";
    }

    if (rawValue.includes("fridge") || rawValue.includes("refrigerator")) {
      return "fridge";
    }

    if (rawValue.includes("ac") || rawValue.includes("air conditioner")) {
      return "ac";
    }

    return service?.id;
  };

  const handleServiceClick = (service) => {
    setActiveTab(service);
    const route = serviceRoutes[service.id];
    if (route) {
      navigate(route);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const loadServices = async () => {
      try {
        const res = await adminApi.get("/api/services");
        const apiServices = Array.isArray(res.data)
          ? res.data
              .map((service, index) => ({
                id: service._id || `service-${index}`,
                name: service.h1 || service.title || "Appliance Repair Service",
                icon: index % 2 === 0 ? <FaTools /> : <FaSnowflake />,
                color: index % 2 === 0 ? "#0F52BA" : "#FF6B00",
                types:
                  Array.isArray(service.types) && service.types.length
                    ? service.types
                    : ["All major appliance models"],
                issues:
                  Array.isArray(service.issues) && service.issues.length
                    ? service.issues
                    : ["Inspection and repair support available"],
              }))
              .filter((service) => service.name)
          : [];

        if (!isMounted) {
          return;
        }

        // Only update if API returns data, otherwise keep fallback
        if (apiServices.length > 0) {
          setServices(apiServices);
          setActiveTab(apiServices[0]);
        }
      } catch (error) {
        console.log("Failed to load services, using fallback:", error);
        // Keep fallback services on error
        if (isMounted) {
          setServices(fallbackServices);
          setActiveTab(fallbackServices[0]);
        }
      }
    };

    loadServices();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <section className="py-24 bg-[#F9FAFB] relative overflow-hidden">
      <div className="absolute top-0 right-0 w-1/3 h-full bg-[#eef4ff] -z-10 rounded-l-[100px] hidden lg:block" />

      <div className="container mx-auto px-6 lg:px-16">
        <div className="mb-16">
          <h2 className="text-4xl lg:text-6xl font-black text-slate-900 leading-tight">
            Our <span className="text-[#0F52BA]">Appliance Repair</span>{" "}
            Services
          </h2>
          <div className="h-2 w-24 bg-[#FF6B00] mt-6 rounded-full" />
        </div>

        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4 space-y-4">
            {services.map((service) => (
              <button
                key={service.id}
                onClick={() => handleServiceClick(service)}
                className={`w-full flex items-center gap-4 p-6 rounded-2xl transition-all duration-300 border-2 ${
                  activeTab.id === service.id
                    ? "bg-white border-[#0F52BA] shadow-xl scale-105"
                    : "bg-transparent border-transparent grayscale opacity-60 hover:opacity-100"
                }`}
              >
                <div
                  className="p-4 rounded-xl text-2xl bg-white shadow-sm"
                  style={{ color: service.color }}
                >
                  {service.icon}
                </div>
                  <span className="font-bold text-xl text-slate-800">
                    {service.name}
                  </span>
              </button>
            ))}
          </div>

          <div className="lg:col-span-8">
            <div className="bg-[#1F2937] rounded-[2.5rem] p-8 lg:p-12 text-white min-h-[500px] relative overflow-hidden group">
              <div className="absolute -bottom-10 -right-10 text-[200px] opacity-10 rotate-12 transition-transform group-hover:rotate-0 duration-700">
                {activeTab.icon}
              </div>

              <div className="relative z-10 h-full flex flex-col justify-between">
                <div>
                  <h3 className="text-3xl lg:text-5xl font-bold mb-6 flex items-center gap-4">
                    {activeTab.name}{" "}
                    <span className="text-[#FF6B00] font-light text-2xl">
                      Specialist
                    </span>
                  </h3>

                  <div className="grid md:grid-cols-2 gap-10 mt-10">
                    <div className="space-y-4">
                      <p className="text-[#FF6B00] font-mono text-sm tracking-widest uppercase italic">
                        Supported Models
                      </p>
                      <ul className="space-y-3">
                        {activeTab.types.map((type, idx) => {
                          const serviceKey = getServiceKey(activeTab);

                          return (
                            <li
                              key={idx}
                              className="flex items-center gap-3 text-lg font-medium text-gray-400"
                            >
                              <FaCheckCircle className="text-emerald-500" />
                              {typeRoutes[serviceKey]?.[type] ? (
                                <Link
                                  to={typeRoutes[serviceKey][type]}
                                  className="text-left transition-colors hover:text-white"
                                >
                                  {type}
                                </Link>
                              ) : (
                                <span>{type}</span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>

                    <div className="space-y-4">
                      <p className="text-[#FF6B00] font-mono text-sm tracking-widest uppercase italic">
                        Common Issues Fixed
                      </p>
                      <ul className="space-y-3">
                        {activeTab.issues.map((issue, idx) => {
                          const serviceKey = getServiceKey(activeTab);

                          return (
                            <li
                              key={idx}
                              className="flex items-center gap-3 text-slate-300"
                            >
                              <div className="w-1.5 h-1.5 bg-[#FF6B00] rounded-full" />
                              {issueRoutes[serviceKey]?.[issue] ? (
                                <Link
                                  to={issueRoutes[serviceKey][issue]}
                                  className="text-left transition-colors hover:text-white"
                                >
                                  {issue}
                                </Link>
                              ) : (
                                <span>{issue}</span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="mt-12 flex flex-col sm:flex-row gap-6 items-center">
                  <button
                    onClick={triggerBookingPopup}
                    className="bg-[#FF6B00] hover:bg-[#d95b00] text-white px-10 py-4 rounded-full font-bold transition-all flex items-center gap-3 group/btn"
                  >
                    Book Repair Now
                    <FaArrowRight className="group-hover/btn:translate-x-2 transition-transform" />
                  </button>
             
                  <p className="text-zinc-500 text-sm italic" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
